import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { PackagePurchasesService } from '../../services/payments/package-purchases/package-purchases.service';
import { PackagesService } from '../../services/payments/packages/packages.service';
import { SuccessfulPurchaseRequest } from '../../models/payment/successfulPurchaseRequest';
import { SuccessfulPurchaseResponse } from '../../models/payment/successfulPurchaseResponse';
import { OperationsPersonResponse } from '../../models/operations/operationsPersonResponse';

interface PackageOption {
  id: number;
  name: string;
}

interface CounsellorOption {
  userId: string;
  label: string;
}

@Component({
  selector: 'app-package-purchases',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    TableModule,
    DialogModule,
    ToastModule,
    TooltipModule,
    ProgressSpinnerModule
  ],
  providers: [MessageService],
  templateUrl: './package-purchases.component.html',
  styleUrl: './package-purchases.component.scss'
})
export class PackagePurchasesComponent implements OnInit {
  filterForm!: FormGroup;
  purchases: SuccessfulPurchaseResponse[] = [];
  packages: PackageOption[] = [];
  counsellors: CounsellorOption[] = [];

  isLoading = false;
  showFilters = true;

  totalRecords = 0;
  first = 0;
  rowSize = 10;

  // Assign counsellor dialog
  showAssignDialog = false;
  isAssigning = false;
  selectedPurchase: SuccessfulPurchaseResponse | null = null;
  selectedCounsellorId: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private packagePurchasesService: PackagePurchasesService,
    private packagesService: PackagesService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadPackages();
    this.loadCounsellors();
    this.loadPurchases(1);
  }

  initializeForm(): void {
    this.filterForm = this.formBuilder.group({
      studentName: [null],
      studentEmail: [null],
      packageId: [null],
      counsellorId: [null],
      dateRange: [null]
    });
  }

  loadPackages(): void {
    this.packagesService.getAllPackages({
      name: '',
      servicesIncluded: [''],
      duration: 0,
      amount: 0,
      description: ''
    }).subscribe({
      next: (data) => {
        const arr = Array.isArray(data) ? data : (data as any)?.result ?? [];
        this.packages = arr.map((item: any) => ({
          id: item.Id ?? item.id ?? 0,
          name: item.Name ?? item.name ?? ''
        }));
        this.mapPackageNames();
      },
      error: (err) => console.error('Error loading packages:', err)
    });
  }

  loadCounsellors(): void {
    this.packagePurchasesService.getOperationsPeople().subscribe({
      next: (response) => {
        const arr = response?.result ?? [];
        this.counsellors = arr.map((c: OperationsPersonResponse) => ({
          userId: c.userId,
          label: this.buildCounsellorLabel(c)
        }));
      },
      error: (err) => console.error('Error loading counsellors:', err)
    });
  }

  private buildCounsellorLabel(c: OperationsPersonResponse): string {
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    if (name && c.email) {
      return `${name} (${c.email})`;
    }
    return name || c.email || c.userId;
  }

  buildRequest(pageNumber: number): SuccessfulPurchaseRequest {
    const formValue = this.filterForm.value;
    const dateRange = formValue.dateRange as Date[] | null;

    return {
      studentName: formValue.studentName || null,
      studentEmail: formValue.studentEmail || null,
      packageId: formValue.packageId || null,
      counsellorId: formValue.counsellorId || null,
      dateFrom: dateRange && dateRange[0] ? this.formatDate(dateRange[0]) : null,
      dateTo: dateRange && dateRange[1] ? this.formatDate(dateRange[1]) : null,
      pageNumber: pageNumber,
      pageSize: this.rowSize
    };
  }

  private formatDate(date: Date): string | null {
    if (!date) return null;
    try {
      return new Date(date).toISOString();
    } catch {
      return null;
    }
  }

  loadPurchases(pageNumber: number): void {
    this.isLoading = true;
    this.packagePurchasesService.listPackagePurchases(this.buildRequest(pageNumber)).subscribe({
      next: (response) => {
        this.purchases = response?.result ?? [];
        this.totalRecords = response?.total ?? 0;
        this.mapPackageNames();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load package purchases' });
      }
    });
  }

  private mapPackageNames(): void {
    if (this.purchases && this.purchases.length > 0) {
      this.purchases.forEach((purchase: any) => {
        if (purchase.purchasedPackages && purchase.purchasedPackages.length > 0) {
          purchase.packageName = purchase.purchasedPackages.map((p: any) => p.packageName).join(', ');
        }
      });
    }
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.rowSize = event.rows ?? this.rowSize;
    const pageNumber = Math.floor(this.first / this.rowSize) + 1;
    this.loadPurchases(pageNumber);
  }

  onFilter(): void {
    this.first = 0;
    this.loadPurchases(1);
  }

  clearAllFilters(): void {
    this.filterForm.reset({
      studentName: null,
      studentEmail: null,
      packageId: null,
      counsellorId: null,
      dateRange: null
    });
    this.onFilter();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  openAssignDialog(purchase: SuccessfulPurchaseResponse): void {
    this.selectedPurchase = purchase;
    this.selectedCounsellorId = purchase.counsellorId ?? null;
    this.showAssignDialog = true;
  }

  closeAssignDialog(): void {
    this.showAssignDialog = false;
    this.selectedPurchase = null;
    this.selectedCounsellorId = null;
  }

  submitAssignment(): void {
    if (!this.selectedPurchase || !this.selectedCounsellorId) {
      return;
    }

    this.isAssigning = true;
    const request = {
      studentId: this.selectedPurchase.studentId,
      counsellorId: this.selectedCounsellorId
    };

    const existingId = this.selectedPurchase.studentCounsellorId;
    const call$ = existingId
      ? this.packagePurchasesService.updateCounsellor(existingId, request)
      : this.packagePurchasesService.assignCounsellor(request);

    call$.subscribe({
      next: () => {
        this.isAssigning = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: existingId ? 'Counsellor updated successfully' : 'Counsellor assigned successfully'
        });
        this.closeAssignDialog();
        const pageNumber = Math.floor(this.first / this.rowSize) + 1;
        this.loadPurchases(pageNumber);
      },
      error: () => {
        this.isAssigning = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save counsellor assignment' });
      }
    });
  }
}
